import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThemeProvider } from 'react-jss';
import { AssetEventRecordType, AssetType, POST_ASSET_EVENT_NAME } from '@bingads-webui-campaign-react/copilot-common';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

import { getTextAssetRecommendationsStyles } from './style';
import { InlineRecommendations } from './inline-recommendation';
import { maxHeadlineCount, maxDescriptionCount, DKIThemeDefaultName } from './config/text-asset-collection-config';
import { RecommendationContext } from './recommendation-context';

const recommendationFieldMap = {
  Headlines: 'headlines',
  Descriptions: 'descriptions',
};
const assetCountMap = {
  Headlines: maxHeadlineCount,
  Descriptions: maxDescriptionCount,
};

export const TextAssetCollectionWithRecommendations = React.memo((props) => {
  const {
    validationPropsField,
    maxCollectionSize,
    inlineRecommendationsTitle,
    inlineRecommendationsHelpTopicKey,
    inlineRecommendationsHelpTopicAriaLabel,
    inlineRecommendationsDisabledItemTooltip,
    singleAssetValidationPropsField,
    i18n,
    withInlineRecommendations,
    data,
    onChange,
    updateSuggestionMeasurement,
    setApplyTextAssetCallback,
    enableDKI,
    DKIThemeName,
  } = props;

  const permissions = useContext(PermissionsContext);
  const classes = useDefaultStyles(getTextAssetRecommendationsStyles);
  const currentCollection = _.result(data, validationPropsField, []); // Currently filled headlines
  const currentCollectionAsset = _.pluck(currentCollection, 'Asset').filter(item => (item !== undefined && !_.isNull(item)));
  const currentCollectionAssetText = _.pluck(currentCollectionAsset, 'Text');
  const [viewedCopilotSuggestions, setViewedCopilotSuggestions] = useState();

  const collection = {};
  currentCollectionAssetText.forEach((item, index) => { collection[`Headline${index}`] = item; });

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const { postAssetEventLog = _.noop, copilotTracking } = recommendationContext;

  const logCopilotSuggestions = useCallback((suggestionIds) => {
    if (copilotTracking) {
      const suggestionsToLog = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
      if (suggestionsToLog.length > 0) {
        postAssetEventLog({
          EventName: POST_ASSET_EVENT_NAME,
          EventValue: {
            AssetType: _.map(suggestionsToLog, () => AssetType.TEXT),
            SuggestionIds: suggestionsToLog,
            RecordType: AssetEventRecordType.VIEWED,
          },
        });
        copilotTracking.trackViewedSuggestionIds(suggestionsToLog);
      }
    }
  }, [copilotTracking, postAssetEventLog]);

  const recommendationsField = recommendationFieldMap[validationPropsField];
  const collectionRecommendations = useMemo(() => {
    const { themes } = recommendationContext;
    return _.map(themes, theme => ({
      name: theme.name,
      assets: _.result(theme, recommendationsField),
    }));
  }, [recommendationContext, recommendationsField]);

  const availableRecommendations = useMemo(
    () => {
      let availableCollectionRecommendations = collectionRecommendations.filter(theme => theme.name !== DKIThemeDefaultName);
      if (permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && enableDKI) {
        const DKItheme = collectionRecommendations.filter(theme => theme.name === DKIThemeDefaultName);
        availableCollectionRecommendations = [...DKItheme, ...availableCollectionRecommendations];
      }
      return _.chain(availableCollectionRecommendations)
        .map(theme => ({
          name: theme.name === DKIThemeDefaultName ? DKIThemeName : theme.name,
          assets: _.chain(theme.assets)
            .map(_.property('Text'))
            .filter(recommendation => !_.contains(currentCollectionAssetText, recommendation))
            .value(),
        }))
        .filter(theme => !_.isEmpty(theme.assets))
        .value();
    },
    [DKIThemeName, collectionRecommendations, currentCollectionAssetText, enableDKI, permissions]
  );

  const flattenedCollectionRecommendations = useMemo(() => _.chain(collectionRecommendations)
    .map(theme => _.result(theme, 'assets'))
    .flatten()
    .compact()
    .value(), [collectionRecommendations]);

  const recommendationsMap = useMemo(
    () => _.chain(flattenedCollectionRecommendations)
      .map(recommendation => [
        recommendation.Text,
        {
          ...recommendation,
          SuggestionId: recommendation.RecommendationId,
        },
      ])
      .object()
      .value(),
    [flattenedCollectionRecommendations]
  );

  const getSuggestionIds = useCallback((recommendations) => {
    const displayedRecommendations = _.chain(recommendations)
      .map(recommendation => recommendationsMap[recommendation])
      .compact()
      .value();
    if (!_.isEmpty(displayedRecommendations)) {
      return _.pluck(displayedRecommendations, 'SuggestionId');
    }
    return [];
  }, [recommendationsMap]);

  const reportDisplayedRecommendations = useCallback((recommendations) => {
    const displayedRecommendationsIds = getSuggestionIds(recommendations);
    reportRecommendations({
      view: displayedRecommendationsIds,
    });
    logCopilotSuggestions(displayedRecommendationsIds);
  }, [getSuggestionIds, logCopilotSuggestions, reportRecommendations]);

  const addAssetSuggestion = useCallback((assets, text) => {
    const applySuggestionIds = getSuggestionIds([text]);
    if (_.isEmpty(assets)) {
      onChange([{
        Asset: {
          '@odata.type': '#Model.TextAsset',
          AssetType: 'Text',
          Name: null,
          Text: text,
        },
        PinnedField: 'None',
      }], validationPropsField);
      return;
    }
    const index = _.findIndex(_.range(assets.length), i =>
      _.isEmpty(assets[i].Asset.Text));

    // add suggestion to empty field first; if no empty field, try to add a new field
    if (index !== -1) {
      const newAssets = [...assets];
      newAssets[index].Asset.Text = text;
      onChange(newAssets, validationPropsField);
    } else if (assets.length < assetCountMap[validationPropsField]) {
      const newAssets = [
        ...assets,
        {
          Asset: {
            '@odata.type': '#Model.TextAsset',
            AssetType: 'Text',
            Name: null,
            Text: text,
          },
          PinnedField: 'None',
        },
      ];
      onChange(newAssets, validationPropsField);
    }
    updateSuggestionMeasurement({ applySuggestionIds }); // todo: get real id
  }, [validationPropsField, getSuggestionIds, onChange, updateSuggestionMeasurement]);

  const applyRecommendation = useCallback((key, text) => {
    const collectionData = cloneDeep(currentCollection);
    addAssetSuggestion(collectionData, text);
  }, [addAssetSuggestion, currentCollection]);

  const isAbleToAddSuggestion = useCallback(() => {
    if (_.isEmpty(currentCollection)) return true;
    // has at least one empty field
    return _.some(currentCollection, asset => _.isEmpty(asset.Asset.Text)) ||
      // not reach the asset count limit
      currentCollection.length < assetCountMap[validationPropsField];
  }, [validationPropsField, currentCollection]);

  useEffect(() => {
    if (viewedCopilotSuggestions && recommendationContext) {
      reportDisplayedRecommendations(viewedCopilotSuggestions);
    }
  }, [recommendationContext, reportDisplayedRecommendations, viewedCopilotSuggestions]);

  useEffect(() => {
    setClarityTag({ RSANewCopilotUI: 'true' });
  }, []);

  const addAllAssetSuggestion = useCallback((assets, texts) => {
    let newAssets = [...assets];
    const applySuggestionIds = getSuggestionIds(texts);

    // Track which auto-filled suggestions have been viewed
    const newViewedCopilotSuggestions = [];
    _.each(texts, (text) => {
      if (!isAbleToAddSuggestion()) { return; }
      // dedup
      if (_.some(newAssets, asset => _.result(asset, ['Asset', 'Text']) === text)) { return; }
      if (_.isEmpty(newAssets)) {
        newAssets = [{
          Asset: {
            '@odata.type': '#Model.TextAsset',
            AssetType: 'Text',
            Name: null,
            Text: text,
          },
          PinnedField: 'None',
        }];
        return;
      }
      const index = _.findIndex(_.range(newAssets.length), i =>
        _.isEmpty(newAssets[i].Asset.Text));

      // add suggestion to empty field first; if no empty field, try to add a new field
      if (index !== -1) {
        newAssets[index].Asset.Text = text;
        newViewedCopilotSuggestions.push(text);
      } else if (newAssets.length < assetCountMap[validationPropsField]) {
        newAssets = [
          ...newAssets,
          {
            Asset: {
              '@odata.type': '#Model.TextAsset',
              AssetType: 'Text',
              Name: null,
              Text: text,
            },
            PinnedField: 'None',
          },
        ];
        newViewedCopilotSuggestions.push(text);
      }
    });
    onChange(newAssets, validationPropsField);
    updateSuggestionMeasurement({ applySuggestionIds });
    setViewedCopilotSuggestions(newViewedCopilotSuggestions);
  }, [getSuggestionIds, onChange, validationPropsField, updateSuggestionMeasurement, isAbleToAddSuggestion]);

  const applyRecommendations = useCallback((recommendations) => {
    const collectionData = cloneDeep(currentCollection);
    addAllAssetSuggestion(collectionData, recommendations);
  }, [addAllAssetSuggestion, currentCollection]);

  useEffect(() => {
    if (_.isFunction(setApplyTextAssetCallback)) {
      setApplyTextAssetCallback(() => (recommendations) => {
        applyRecommendations(recommendations);
      });
    }
  }, [setApplyTextAssetCallback, applyRecommendations]);

  return (
    <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
      <div className={classnames('text-asset-recommendation-content', classes.textAssetRecommendationsContent)}>
        {withInlineRecommendations &&
          <InlineRecommendations
            i18n={i18n}
            collection={collection}
            collectionRecommendations={collectionRecommendations}
            maxCollectionSize={maxCollectionSize}
            availableRecommendations={availableRecommendations}
            singleAssetValidationPropsField={singleAssetValidationPropsField}
            inlineRecommendationsHelpTopicKey={inlineRecommendationsHelpTopicKey}
            inlineRecommendationsHelpTopicAriaLabel={inlineRecommendationsHelpTopicAriaLabel}
            inlineRecommendationsTitle={inlineRecommendationsTitle}
            inlineRecommendationsDisabledItemTooltip={inlineRecommendationsDisabledItemTooltip}
            applyRecommendation={applyRecommendation}
            applyRecommendations={applyRecommendations}
            reportDisplayedRecommendations={reportDisplayedRecommendations}
            enableDKI={permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && enableDKI}
            DKIThemeName={DKIThemeName}
          />
        }
      </div>
    </ThemeProvider>
  );
});

TextAssetCollectionWithRecommendations.propTypes = {
  validationPropsField: PropTypes.string.isRequired,
  data: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  singleAssetValidationPropsField: PropTypes.string.isRequired,
  maxCollectionSize: PropTypes.number.isRequired,
  inlineRecommendationsTitle: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicKey: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicAriaLabel: PropTypes.string.isRequired,
  inlineRecommendationsDisabledItemTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  withInlineRecommendations: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  updateSuggestionMeasurement: PropTypes.func.isRequired,
  setApplyTextAssetCallback: PropTypes.func,
  enableDKI: PropTypes.bool,
  DKIThemeName: PropTypes.string,
};

TextAssetCollectionWithRecommendations.defaultProps = {
  withInlineRecommendations: false,
  setApplyTextAssetCallback: _.noop,
  enableDKI: false,
  DKIThemeName: '',
};
